/**
 * Hero - Main hero section component
 * Replaces BannerBlock with a simplified, content-focused API
 */

import React from 'react'
import { Box, Container, CardMedia, Typography, Button, Link } from '@mui/material'
import { EyeOutlined } from '@ant-design/icons'
import { commonPageStyles } from '../../../shared/common-styles'
import AnimateButton from '../../../components/@extended/AnimateButton'
import NextLink from 'next/link'

interface HeroProps {
	title: string | React.ReactNode
	description: string | React.ReactNode
	buttons?: Array<{
		text: string
		href?: string
		style?: 'primary' | 'secondary' | 'outlined'
		openInNewTab?: boolean
	}>
	backgroundImage?: string
	layout?: 'text-only' | 'text-with-image' | 'centered'
}

const Hero: React.FC<HeroProps> = ({ title, description, buttons = [], backgroundImage, layout = 'text-with-image' }) => {
	return (
		<Box component="section" sx={commonPageStyles.landing}>
			{backgroundImage && (
				<CardMedia
					sx={{
						position: 'absolute',
						width: { md: '78%', lg: '70%', xl: '65%' },
						right: { md: '-14%', lg: '-4%', xl: '-2%' },
						top: { md: '16%', lg: '12%', xl: '8%' },
						zIndex: 1,
						display: { xs: 'none', md: 'block' }
					}}
				>
					<img src={backgroundImage} alt="hero background" width={1500} height={800} style={{ objectFit: 'cover' }} />
				</CardMedia>
			)}

			<Container
				sx={{
					minHeight: '80vh',
					display: 'flex',
					alignItems: 'flex-start',
					textAlign: layout === 'centered' ? 'center' : { xs: 'center', md: 'left' },
					mt: '64px', // Below header
					pt: 4
				}}
			>
				<Box
					sx={{
						flex: layout === 'centered' ? 1 : { xs: 1, md: 0.5 },
						position: 'relative',
						mx: layout === 'centered' ? 'auto' : undefined,
						maxWidth: layout === 'centered' ? { xs: '100%', md: '800px' } : undefined
					}}
				>
					<Typography
						variant="h1"
						color="white"
						sx={{
							fontSize: {
								xs: '2.5rem',
								sm: '2.75rem',
								md: '3rem'
							},
							fontWeight: 700,
							lineHeight: 1.3,
							textAlign: layout === 'centered' ? 'center' : { xs: 'center', md: 'left' }
						}}
					>
						{title}
					</Typography>

					<Typography
						variant="h6"
						component="div"
						color="white"
						sx={{
							fontSize: { xs: '1rem', md: '1.25rem' },
							fontWeight: 400,
							lineHeight: 1.3,
							mt: 2,
							textAlign: layout === 'centered' ? 'center' : { xs: 'center', md: 'left' }
						}}
					>
						{description}
					</Typography>

					{buttons.length > 0 && (
						<Box
							sx={{
								mt: 4,
								display: 'flex',
								flexDirection: { xs: 'column', sm: 'row' },
								justifyContent: layout === 'centered' ? 'center' : { xs: 'center', md: 'flex-start' },
								gap: 2
							}}
						>
							{buttons.map((button, index) => (
								<AnimateButton key={index}>
									<NextLink href={button.href || '#'} passHref target={button.openInNewTab ? '_blank' : undefined}>
										<Button
											size="large"
											color="primary"
											variant={button.style === 'outlined' ? 'outlined' : 'contained'}
											component={Link}
											target={button.openInNewTab ? '_blank' : undefined}
											startIcon={
												button.text === 'Get early access' ? (
													<EyeOutlined
														style={{
															fontSize: '1.15rem'
														}}
													/>
												) : undefined
											}
										>
											{button.text}
										</Button>
									</NextLink>
								</AnimateButton>
							))}
						</Box>
					)}
				</Box>
			</Container>
		</Box>
	)
}

export default Hero
